import { create } from 'zustand'
import axios from 'axios'
import { ASSISTENT_API_URL } from '@/config/backend'

interface Language {
  code: string
  name: string
}

interface SttLanguage {
  code: string
  name: string
}

interface Voice {
  id: string
  name: string
  language: string
}

interface Persona {
  id: string
  name: string
  description?: string
}

interface ConfigurationState {
  languages: Language[]
  sttLanguages: SttLanguage[]
  voices: Voice[]
  personas: Record<string, Persona[]>

  // Actions
  initBaseConfiguration: (headers: Record<string, string>) => Promise<void>
  setConfiguration: (config: Partial<ConfigurationState>) => void
  retrievePersona: (lang: string, headers: Record<string, string>) => Promise<void>
  retrieveGlobalConfiguration: (headers: Record<string, string>) => Promise<void>

  // Getters
  getPersonas: (lang: string) => Persona[] | undefined
}

export const useConfigurationStore = create<ConfigurationState>((set, get) => ({
  languages: [],
  sttLanguages: [],
  voices: [],
  personas: {},

  retrievePersona: async (lang: string, headers: Record<string, string>) => {
    const url = `${ASSISTENT_API_URL}configuration/personas/${lang}`

    try {
      const response = await axios.get(url, { headers })
      
      if (response.data.errorCode === null) {
        set(state => ({
          personas: {
            ...state.personas,
            [lang]: response.data.resultObject
          }
        }))
      }
    } catch (error) {
      console.error('Error retrieving persona for language:', lang, error)
      throw error
    }
  },

  retrieveGlobalConfiguration: async (headers: Record<string, string>) => {
    console.log('⚠️ GLOBAL CONFIG: Starting API call')
    const url = `${ASSISTENT_API_URL}configuration/global`

    console.log('🔑 GLOBAL CONFIG: Headers being sent:', headers)
    console.log('🔑 GLOBAL CONFIG: Token in headers:', headers ? headers['Authorization'] : 'NO HEADERS')
    console.log('🔑 GLOBAL CONFIG: App ID in headers:', headers ? headers['X-AAI-APPLICATIONID'] : 'NO HEADERS')

    try {
      const response = await axios.get(url, { headers })
      
      console.log('✅ GLOBAL CONFIG: Success response received')
      if (response.data.errorCode === null) {
        set({
          languages: response.data.resultObject.availableLanguages,
          sttLanguages: response.data.resultObject.availableSttLanguages,
          voices: response.data.resultObject.availableVoices
        })
      }
    } catch (error: any) {
      console.error('❌ GLOBAL CONFIG: Error:', error.response?.status, error.response?.data)
      console.error('❌ GLOBAL CONFIG: Full error object:', error)
      throw error
    }
  },

  initBaseConfiguration: async (headers: Record<string, string>) => {
    console.log('🔧 INIT CONFIG: Starting with headers:', headers)
    const { retrieveGlobalConfiguration, retrievePersona } = get()
    
    await retrieveGlobalConfiguration(headers)

    // Get updated languages from state after global config is loaded
    const currentLanguages = get().languages
    await Promise.all(
      currentLanguages.map((lang: Language) => retrievePersona(lang.code, headers))
    )
    
    console.log('✅ INIT CONFIG: Base configuration completed')
  },

  setConfiguration: (config: Partial<ConfigurationState>) => {
    if (config) {
      set(state => ({
        ...state,
        ...config
      }))
    }
  },

  // Getters
  getPersonas: (lang: string) => {
    return get().personas[lang]
  }
}))
